// CX Services — Romanian copy. Imported into ro.ts as `cx`.
// Typed against the English dictionary: a missing key fails the build.
import type { cxEn } from './cx.en';

export const cxRo: typeof cxEn = {
  meta: {
    hubTitle: 'Operațiuni Customer Experience din UE — ADD Individual Solutions',
    hubDescription: 'Suport clienți în limba germană și multilingv, back-office și CX cu AI, livrate din Cipru și Transilvania sub un singur contract UE. Pilot-first, cu SLA, conform GDPR.',
    assessmentTitle: 'Solicită un CX Operations Assessment — ADD Individual Solutions',
    assessmentDescription: 'O analiză de două săptămâni a operațiunii tale de customer service: volume, canale, KPI și cost per contact. Primești un baseline, un model-țintă și un plan de pilot. Fără obligații.',
  },

  breadcrumbs: { home: 'Acasă', cx: 'Servicii CX', services: 'Linii de servicii', assessment: 'Assessment' },

  hero: {
    eyebrow: 'Operațiuni Customer Experience · UE',
    title: 'Suport clienți care răspunde în fața autorității de reglementare, a CFO-ului și a clienților tăi.',
    subtitle: 'Suport clienți în limba germană și multilingv, back-office și CX cu AI — livrate din Cipru și Transilvania sub un singur contract UE. Fondată și condusă de un operator cu cincisprezece ani la SYKES Enterprises.',
    cta1: 'Solicită un CX Operations Assessment',
    cta2: 'Vezi modelul de livrare',
  },

  needs: {
    tag: 'Ce au nevoie cumpărătorii de CX externalizat',
    title: 'Trei cerințe. Un singur model de livrare.',
    items: [
      { icon: 'shield', title: 'Conformitate fără compromis', text: 'Datele clienților sunt procesate în UE, sub GDPR, fără întrebări despre transferuri în țări terțe din partea DPO-ului sau a autorității de reglementare.' },
      { icon: 'trending', title: 'Disciplină a costului per contact', text: 'Livrare în limba germană cu 25–40% sub tarifele onshore din DACH, cu o structură de cost transparentă per FTE sau per oră.' },
      { icon: 'usercheck', title: 'Continuitatea calității', text: 'Un operator numit, responsabil de cont, livrare garantată prin SLA și o intrare de tip pilot care îți limitează expunerea.' },
    ],
  },

  services: {
    tag: 'Linii de servicii',
    title: 'Patru linii de servicii, un singur cadru de calitate',
    subtitle: 'Suportul în limba germană este nucleul. Suportul multilingv, back-office-ul și CX-ul cu AI sunt grupate în jurul lui, operate de aceeași echipă, cu aceiași KPI.',
    learnMore: 'Detalii serviciu',
    items: [
      { slug: 'german-customer-support', icon: 'headphones', title: 'Suport clienți în limba germană', summary: 'Voce, e-mail, chat și messaging; nivel 1 și 2; reclamații și retenție. Livrat de vorbitori nativi și aproape nativi de germană.', tags: ['Nivel 1 & 2', 'Reclamații', 'Retenție'] },
      { slug: 'multilingual-support', icon: 'globe', title: 'Suport multilingv', summary: 'Engleză ca standard; română, greacă și alte limbi UE la cerere. O singură echipă, un singur cadru de calitate pentru toate piețele.', tags: ['EN standard', 'Limbi UE', 'Un cadru QA'] },
      { slug: 'back-office-data-processing', icon: 'database', title: 'Back-office și procesare date', summary: 'Procesare comenzi, KYC și onboarding, gestionare documente, introducere și verificare date — cu control în patru ochi acolo unde este necesar.', tags: ['KYC', 'Procesare comenzi', 'Patru ochi'] },
      { slug: 'cx-consulting-ai', icon: 'cpu', title: 'Consultanță CX și CX cu AI', summary: 'Assessment operațional, design QA și WFM, programe CSAT/NPS; agent-assist, deflection inteligentă și scoring automat al calității.', tags: ['Assessment', 'Agent assist', 'Automatizare QA'] },
    ],
  },

  delivery: {
    tag: 'Model de livrare',
    title: 'Un singur contract UE, două locații',
    cyprus: {
      title: 'Cipru — hub',
      points: [
        'Membru UE din 2004, euro din 2008; impozit pe profit 15% (2026)',
        'Hub consacrat de servicii reglementate: firme de investiții CySEC/MiFID, iGaming, shipping, turism',
        'Mediu de afaceri în limba engleză; fus orar EET (DACH +1h)',
        'Rol: contract, conformitate, management, asigurarea calității, linii premium în limba germană',
      ],
    },
    transylvania: {
      title: 'Transilvania — livrare',
      points: [
        'România: membru UE, Schengen, GDPR — fără bariere de transfer al datelor',
        'Forță de muncă puternică în engleză, franceză, germană, italiană și spaniolă',
        'Sibiu: cea mai veche școală în limba germană din România (1380); peste 100 de companii cu capital german; investitorii germani reprezintă aproximativ jumătate din locurile de muncă private locale',
        'Rol: capacitate scalabilă și profunzime lingvistică la un cost mai mic',
      ],
    },
    foot: 'Clientul contractează cu entitatea din Cipru. Datele rămân în UE. Managementul și asigurarea calității aparțin operatorului.',
  },

  cyprus: {
    tag: 'De ce Cipru',
    title: 'Baza potrivită pentru operațiuni reglementate cu clienții',
    stats: [
      { value: '2004', label: 'Stat membru UE' },
      { value: '2008', label: 'Membru al zonei euro' },
      { value: '15%', label: 'Impozit pe profit (2026)' },
      { value: '22.000 €', label: 'Prag de venit personal neimpozabil (2026) — un avantaj de recrutare' },
    ],
    body: 'Cipru nu este un experiment în operațiuni cu clienții. Echipe de suport reglementate și multilingve funcționează deja aici pentru brokeri licențiați CySEC, instituții de plată și operatori iGaming care deservesc întreaga UE. Acesta este mediul operațional în care ADD este construită — și cel pe care îl așteaptă cumpărătorii din fintech, trading, travel și e-commerce.',
  },

  sectors: {
    tag: 'Sectoare',
    title: 'Unde rezidența datelor în UE și calitatea limbii decid furnizorul',
    items: [
      { icon: 'creditcard', title: 'Fintech și plăți', text: 'KYC și onboarding, dispute, chargeback-uri, administrarea conturilor — acolo unde rezidența datelor în UE este o cerință fermă.' },
      { icon: 'barchart', title: 'Trading și brokeraj', text: 'Suport pentru clienți reglementat CySEC/BaFin, solicitări de alimentare și retragere, gestionarea reclamațiilor cu pistă de audit.' },
      { icon: 'target', title: 'iGaming', text: 'Suport multilingv 24/7 pentru jucători, fluxuri de responsible gaming, plăți și verificare.' },
      { icon: 'navigation', title: 'Travel și mobilitate', text: 'Rezervări, gestionarea perturbărilor, asistență rutieră și de închiriere în mai multe fusuri orare.' },
      { icon: 'cart', title: 'E-commerce și abonamente', text: 'Procesarea comenzilor și retururilor, reclamații și retenție, scalare în sezonul de vârf.' },
    ],
  },

  operate: {
    tag: 'Cum operăm',
    title: 'Evaluăm, proiectăm, pilotăm, scalăm',
    steps: [
      { n: '1', title: 'Evaluare', text: 'Două săptămâni: volume, canale, KPI, cost per contact, baseline QA.' },
      { n: '2', title: 'Design', text: 'Model-țintă, plan de personal, set de SLA, plan de tranziție.' },
      { n: '3', title: 'Pilot', text: '3–5 FTE, 90 de zile, review-uri săptămânale, clauză de ieșire.' },
      { n: '4', title: 'Scalare', text: 'Creștere în pași conveniți; business review-uri lunare.' },
    ],
    standardsTitle: 'Standarde operaționale',
    standards: [
      'Set de KPI: CSAT, FCR, AHT, respectarea SLA, scor QA — raportate săptămânal',
      'Workforce management: forecasting, planificare, control intraday',
      'Sesiuni de calibrare QA cu clientul; acoperire 100% acolo unde este implementat scoringul automat',
      'Playbook de tranziție: plan 30/60/90 de zile cu responsabili numiți',
      'Securitate: procesare exclusiv în UE, acord de prelucrare a datelor conform GDPR, controale aliniate ISO',
    ],
  },

  commercial: {
    tag: 'Model comercial',
    title: 'Pilot-first, open-book',
    pilot: {
      title: 'Intrare prin pilot',
      points: [
        '3–5 FTE timp de 90 de zile, cu SLA și clauză de ieșire',
        'Structură de cost transparentă per FTE — fără taxe ascunse de setup sau tehnologie',
        'Review-uri săptămânale de performanță pe durata pilotului',
        'Scalare doar în raport cu KPI conveniți',
      ],
    },
    pricing: {
      title: 'Prețuri',
      points: [
        'Per FTE pe lună sau per oră productivă — la alegerea ta',
        'Suport în limba germană: indicativ 27–35 € per oră productivă',
        'Referință: tarife BPO onshore DACH 40–55 €/h; agent intern 55–90 mii € pe an, cost complet',
        'Trepte de volum de la 10 și 20 FTE; prețul final după assessment',
      ],
    },
  },

  ai: {
    tag: 'CX cu AI',
    title: 'Implementat de operatori, măsurat în KPI',
    items: [
      { icon: 'message', title: 'Agent assist', text: 'Răspunsuri redactate de LLM și regăsire de cunoștințe direct în desktopul agentului. Măsurat în AHT și FCR, nu în demonstrații.' },
      { icon: 'layers', title: 'Deflection inteligentă', text: 'Self-service și automatizare conversațională pentru contactele repetitive, cu predare curată către un agent uman.' },
      { icon: 'check', title: 'Calitate cu acoperire 100%', text: 'Scoring automat al fiecărei interacțiuni conform cadrului tău de QA; calibrarea rămâne la oameni.' },
      { icon: 'activity', title: 'Analitică operațională', text: 'Analiza driverelor de contact și forecasting care alimentează planificarea forței de muncă și remedierea cauzelor.' },
    ],
    foot: 'ADD construiește aceste instrumente intern. Ele sunt introduse după ce operațiunea este stabilă, niciodată ca substitut al ei.',
  },

  leadership: {
    tag: 'Conducere',
    title: 'Condusă de un operator, nu de o echipă de vânzări',
    name: 'Daniel Dobos',
    role: 'Founder & Managing Director · Diplom-Kaufmann',
    lead: 'Cincisprezece ani la SYKES Enterprises, cel mai recent Senior Operations Manager pentru România și Cipru (Cluj-Napoca, Larnaca), conducând conturi în limba germană de peste 280 FTE.',
    facts: [
      'Deutsche Telekom — front- și back-office în limba germană',
      '1&1 — front- și back-office în limba germană',
      'Klarna — nivel 1 și 2, germană și engleză',
      'PlayStation — germană, limbi nordice și turcă',
      'Diplom-Kaufmann: studii universitare germane în științe economice',
    ],
    meansTitle: 'Ce înseamnă asta pentru programul tău',
    means: [
      'Ramp-up-uri, tranziții și conducerea site-urilor executate personal — pentru Deutsche Telekom, 1&1, Klarna și PlayStation',
      'Workforce management, calibrare QA și guvernanță KPI aplicate din prima zi',
      'Cale directă de escaladare către persoana responsabilă de rezultatele tale',
    ],
  },

  governance: {
    tag: 'Risc și guvernanță',
    title: 'Riscuri numite, controale documentate',
    items: [
      { icon: 'lock', title: 'Protecția datelor', text: 'Acord de prelucrare a datelor conform GDPR, procesare exclusiv în UE, sub-procesatori documentați, controale de acces și jurnale de audit.' },
      { icon: 'refresh', title: 'Continuitatea activității', text: 'Două locații în UE, echipe capabile să lucreze remote, failover documentat pentru liniile în limba germană.' },
      { icon: 'users', title: 'Personal', text: 'Recrutare pe două piețe ale muncii; pipeline de vorbitori de germană în Transilvania; rezervă pentru fluctuație inclusă în plan.' },
      { icon: 'eye', title: 'Transparență', text: 'Pilot open-book, review-uri săptămânale, raportare KPI pe care o poți verifica.' },
      { icon: 'logout', title: 'Ieșire', text: 'Predare documentată a bazei de cunoștințe, a proceselor și a datelor — convenită înainte de începerea pilotului.' },
    ],
  },

  cta: {
    title: 'Pasul următor: un CX Operations Assessment de două săptămâni',
    subtitle: 'Analizăm volume, canale, KPI și cost per contact. Primești un baseline, un model-țintă și un plan de pilot cu personal, SLA și preț. Fără obligația de a continua.',
    button: 'Solicită assessment-ul',
    secondary: 'Sau scrie la contact@add-individual-solutions.com',
  },

  teaser: {
    tag: 'Operațiuni Customer Experience',
    title: 'Suport clienți din UE, condus de un operator',
    text: 'Suport clienți în limba germană și multilingv, back-office și CX cu AI — livrate din Cipru și Transilvania sub un singur contract UE.',
    points: ['Procesare exclusiv în UE, sub GDPR', '25–40% sub tarifele onshore din DACH', 'Pilot-first: 3–5 FTE, 90 de zile, clauză de ieșire'],
    cta: 'Descoperă serviciile CX',
  },

  assessment: {
    title: 'Solicită un CX Operations Assessment',
    subtitle: 'Două săptămâni. Volumele, canalele, KPI și costul tău per contact, analizate de un operator. Păstrezi baseline-ul indiferent dacă mergi mai departe sau nu.',
    review: {
      title: 'Ce analizăm',
      items: ['Volume de contacte, canale și drivere', 'Performanța AHT, FCR, CSAT și SLA', 'Cost per contact și per FTE', 'Cadrul QA și calibrarea'],
    },
    receive: {
      title: 'Ce primești',
      items: ['Un baseline al operațiunii tale actuale', 'Un model-țintă pentru scopul externalizat', 'Un plan de pilot cu personal, SLA și preț', 'Fără obligația de a continua'],
    },
    form: {
      title: 'Spune-ne despre operațiunea ta',
      companySection: 'Companie',
      contactSection: 'Contact',
      operationSection: 'Operațiune',
      companyName: 'Numele companiei',
      website: 'Website',
      industry: 'Sector',
      industryOptions: [
        { value: '', label: 'Alege un sector' },
        { value: 'fintech', label: 'Fintech și plăți' },
        { value: 'trading', label: 'Trading și brokeraj' },
        { value: 'igaming', label: 'iGaming' },
        { value: 'travel', label: 'Travel și mobilitate' },
        { value: 'ecommerce', label: 'E-commerce și abonamente' },
        { value: 'medtech', label: 'Medtech / domeniul sănătății' },
        { value: 'saas', label: 'SaaS / tehnologie' },
        { value: 'other', label: 'Altul' },
      ],
      country: 'Țara',
      contactName: 'Numele tău',
      jobTitle: 'Funcția',
      email: 'E-mail de business',
      phone: 'Telefon (opțional)',
      fteBand: 'FTE în customer service astăzi',
      fteOptions: [
        { value: '', label: 'Alege un interval' },
        { value: '1-10', label: '1–10' },
        { value: '11-25', label: '11–25' },
        { value: '26-50', label: '26–50' },
        { value: '51-100', label: '51–100' },
        { value: '101-250', label: '101–250' },
        { value: '250+', label: 'Peste 250' },
      ],
      languages: 'Limbi necesare',
      languageOptions: [
        { value: 'de', label: 'Germană' },
        { value: 'en', label: 'Engleză' },
        { value: 'ro', label: 'Română' },
        { value: 'other', label: 'Alta' },
      ],
      channels: 'Canale în scop',
      channelOptions: [
        { value: 'voice', label: 'Voce' },
        { value: 'email', label: 'E-mail' },
        { value: 'chat', label: 'Chat / messaging' },
        { value: 'backoffice', label: 'Back-office' },
      ],
      currentModel: 'Modelul actual de livrare',
      modelOptions: [
        { value: '', label: 'Alege' },
        { value: 'in_house', label: 'Intern' },
        { value: 'outsourced_onshore', label: 'Externalizat — onshore' },
        { value: 'outsourced_nearshore', label: 'Externalizat — nearshore / offshore' },
        { value: 'mixed', label: 'Mixt' },
      ],
      timeline: 'Orizont de timp',
      timelineOptions: [
        { value: '', label: 'Alege' },
        { value: 'immediate', label: 'Imediat' },
        { value: '1-3_months', label: '1–3 luni' },
        { value: '3-6_months', label: '3–6 luni' },
        { value: 'exploring', label: 'În explorare' },
      ],
      message: 'Context (opțional)',
      messagePlaceholder: 'Probleme actuale, sisteme folosite, orice ar trebui să știm înainte de discuția de scoping.',
      consent: 'Sunt de acord ca ADD Individual Solutions Ltd să prelucreze datele de mai sus pentru a răspunde acestei solicitări, conform',
      consentLink: 'Politicii de confidențialitate',
      submit: 'Solicită assessment-ul',
      sending: 'Se trimite…',
      verify: 'Te rugăm să completezi verificarea.',
      success: 'Solicitarea ta a fost primită. Daniel Dobos te va contacta în maximum o zi lucrătoare.',
      error: 'Solicitarea nu a putut fi trimisă. Încearcă din nou sau scrie la contact@add-individual-solutions.com.',
      required: 'Obligatoriu',
    },
  },

  servicePages: {
    'german-customer-support': {
      title: 'Suport clienți în limba germană',
      metaDescription: 'Suport clienți în limba germană din UE — voce, e-mail, chat și messaging, nivel 1 și 2, reclamații și retenție. Livrat din Cipru și Transilvania de ADD Individual Solutions.',
      heroDescription: 'Agenți vorbitori nativi și aproape nativi de germană, conduși sub un singur cadru de calitate din două locații UE. Nivel 1 și 2, reclamații și retenție, pe voce, e-mail, chat și messaging — cu setul de KPI, calibrarea QA și workforce management-ul pe care le așteaptă o operațiune de customer service din DACH.',
      sections: [
        { heading: 'Scop', content: 'Customer service inbound și nivel 1 tehnic; gestionarea cazurilor de nivel 2; managementul reclamațiilor și al escaladărilor; retenție și win-back; suport pentru comenzi, conturi și facturare. Acoperire de la program de lucru la 24/7 în pași conveniți, în fusul orar EET (DACH +1h).' },
        { heading: 'Cum livrăm', content: 'Echipele sunt recrutate pe două piețe ale muncii — Cipru și Transilvania — și conduse din hub-ul din Cipru. Fiecare program începe cu un assessment de două săptămâni și un plan de tranziție documentat (30/60/90 de zile). Workforce management-ul acoperă forecasting, planificare și control intraday; team leaderii lucrează la un raport de unu la nouă.' },
        { heading: 'Calitate și conformitate', content: 'CSAT, FCR, AHT, respectarea SLA și scorul QA sunt raportate săptămânal. Sesiunile de calibrare QA se desfășoară împreună cu echipa ta; acolo unde este implementat scoringul automat, acoperirea este de 100% din interacțiuni. Procesarea se face exclusiv în UE, sub un acord de prelucrare a datelor conform GDPR, cu sub-procesatori documentați și controale de acces.' },
        { heading: 'Condiții comerciale', content: 'Pilot de 3–5 FTE timp de 90 de zile, cu SLA și clauză de ieșire. Preț per FTE pe lună sau per oră productivă — indicativ 27–35 € per oră productivă, față de tarife onshore DACH de 40–55 €. Trepte de volum de la 10 și 20 FTE. Prețul final urmează assessment-ului.' },
      ],
    },
    'multilingual-support': {
      title: 'Suport multilingv',
      metaDescription: 'Suport clienți multilingv din UE — engleză ca standard, română, greacă și alte limbi UE la cerere, sub un singur cadru de calitate. ADD Individual Solutions, Cipru și Transilvania.',
      heroDescription: 'O singură echipă, un singur cadru de calitate pentru toate piețele. Engleza este standard; româna și greaca sunt disponibile din cele două locații ale noastre; alte limbi UE se adaugă la cerere prin același proces de recrutare și QA.',
      sections: [
        { heading: 'Scop', content: 'Customer service, nivel 1 tehnic și back-office în engleză și limbi suplimentare, pentru companii care deservesc mai multe piețe europene dintr-o singură operațiune. Acoperirea lingvistică și orele se convin per linie și pot fi extinse în pași.' },
        { heading: 'Cum livrăm', content: 'Liniile multilingve împart supervizarea, workforce management-ul și QA-ul cu nucleul în limba germană, ceea ce menține scăzut costul per linie de limbă. Cipru oferă capacitate în engleză și greacă; Transilvania oferă română, germană și alte limbi, dintr-o forță de muncă puternică în engleză, franceză, germană, italiană și spaniolă.' },
        { heading: 'Calitate și conformitate', content: 'Același set de KPI se aplică fiecărei limbi: CSAT, FCR, AHT, respectarea SLA și scorul QA, raportate săptămânal. Calitatea lingvistică este evaluată la recrutare și reverificată în calibrarea QA. Procesare exclusiv în UE, sub GDPR, cu o listă documentată de sub-procesatori.' },
        { heading: 'Condiții comerciale', content: 'Per FTE pe lună sau per oră productivă, cu trepte de volum de la 10 și 20 FTE. Liniile de limbă sunt tarifate individual; engleza și româna sunt cele mai eficiente din punct de vedere al costului. Intrarea prin pilot, cu clauză de ieșire, se aplică fiecărei linii.' },
      ],
    },
    'back-office-data-processing': {
      title: 'Back-office și procesare date',
      metaDescription: 'Back-office și procesare date din UE — procesare comenzi, KYC și onboarding, gestionare documente, introducere și verificare date cu control în patru ochi. ADD Individual Solutions.',
      heroDescription: 'Activitate de back-office structurată și măsurabilă, operată sub aceeași guvernanță ca liniile de contact cu clienții: procesare comenzi, KYC și onboarding, gestionare documente, introducere și verificare date — cu control în patru ochi acolo unde acuratețea este contractuală.',
      sections: [
        { heading: 'Scop', content: 'Procesarea comenzilor și retururilor; cozi de KYC, onboarding și verificare; clasificarea documentelor și extragerea datelor; introducere și îmbogățire de date; pregătirea cazurilor pentru procese reglementate. Livrat ca back-office de sine stătător sau împreună cu liniile de front-office.' },
        { heading: 'Cum livrăm', content: 'Activitatea este măsurată per unitate și per oră în raport cu ținte convenite de acuratețe și timp de procesare. Cozile sunt gestionate cu disciplină de workforce management; vârfurile sunt absorbite de echipa comună. Verificarea în patru ochi se aplică tipurilor de tranzacții definite, iar fiecare unitate de lucru este trasabilă.' },
        { heading: 'Calitate și conformitate', content: 'Acuratețea, timpul de procesare și rata de refacere sunt raportate săptămânal. Procesare exclusiv în UE, sub un acord de prelucrare a datelor conform GDPR; acces bazat pe roluri; jurnale de audit pe fiecare coadă. Procesele reglementate (KYC, plăți) urmează procedurile tale scrise și regulile de aprobare.' },
        { heading: 'Condiții comerciale', content: 'Per FTE pe lună sau per oră productivă; preț per unitate disponibil pentru cozi stabile, bine definite. Capacitatea de back-office este tarifată sub liniile în limba germană și este cea mai economică la pachet. Intrare prin pilot, cu clauză de ieșire.' },
      ],
    },
    'cx-consulting-ai': {
      title: 'Consultanță CX și CX cu AI',
      metaDescription: 'Consultanță CX și operațiuni cu clienții susținute de AI — assessment operațional, design QA și WFM, programe CSAT/NPS, agent assist, deflection inteligentă și scoring automat al calității. ADD Individual Solutions.',
      heroDescription: 'Consultanță operațională de la un operator și instrumente AI construite intern, măsurate în KPI. Assessment-ul stabilește baseline-ul; designul QA, WFM și CSAT/NPS stabilește modelul operațional; agent assist, deflection și scoringul automat sunt introduse după ce operațiunea este stabilă.',
      sections: [
        { heading: 'Scop', content: 'CX Operations Assessment de două săptămâni (volume, canale, KPI, cost per contact, baseline QA); designul cadrelor de QA, al proceselor de workforce management și al programelor CSAT/NPS; planificarea tranziției pentru operațiuni interne sau externalizate. CX cu AI: agent assist, deflection inteligentă, scoring al calității cu acoperire 100% și analitică a driverelor de contact.' },
        { heading: 'Cum livrăm', content: 'Consultanța este livrată de operatorul care ar conduce programul, nu de o practică separată. Instrumentele AI sunt dezvoltate de echipa de inginerie a ADD și implementate în desktopul și baza de cunoștințe existente, cu predarea către om proiectată de la început. Fiecare instrument are un KPI de care răspunde.' },
        { heading: 'Calitate și conformitate', content: 'Componentele AI sunt măsurate în AHT, FCR și scor QA, nu în demonstrații. Modelele rulează pe infrastructură din UE; datele clienților rămân sub acordul tău de prelucrare a datelor. Calibrarea și judecata finală asupra calității rămân la oameni.' },
        { heading: 'Condiții comerciale', content: 'Assessment-ul este gratuit pentru operațiuni calificate de 20 FTE sau mai mult și se încheie cu un plan de pilot și un preț. Proiectele de consultanță sunt dimensionate per proiect. CX-ul cu AI este tarifat ca parte a operațiunii pe care o susține, după ce pilotul s-a stabilizat.' },
      ],
    },
  },
};
